import {
  AnimatePresence,
  motion,
  type TargetAndTransition,
  type VariantLabels,
} from 'motion/react';
import { type SkeletonProps } from './props';
import { cn } from '@nosinovacao/nosid-mfe-common';
import React from 'react';

const animationsForLoading: Record<NonNullable<SkeletonProps['loadingAnimation']>, string> = {
  shimmer:
    'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-nos-neutral-75 before:to-transparent',
};

interface MotionAnimation {
  initial: TargetAndTransition | VariantLabels;
  animate: TargetAndTransition | VariantLabels;
  exit: TargetAndTransition | VariantLabels;
}

const onLoadAnimations: Record<
  NonNullable<SkeletonProps['onLoadAnimation']>,
  {
    skeleton: MotionAnimation;
    content: MotionAnimation;
  }
> = {
  fade: {
    skeleton: {
      initial: { opacity: 1 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    content: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 1 },
    },
  },
};

export function SkeletonWoo({
  loading,
  className,
  loadingAnimation = 'shimmer',
  onLoadAnimation = 'fade',
  children,
  onLoadDuration = 0.2,
  loaderChildren,
  count = 1,
}: Readonly<SkeletonProps>) {
  return (
    <AnimatePresence initial={false} mode="wait">
      {loading ? (
        <React.Fragment key="skeleton">
          {Array.from({ length: count })
            .map((_, idx) => idx)
            .map((value) => (
              <motion.div
                key={`skeleton-${value}`}
                className={cn(
                  'bg-nos-neutral-0',
                  animationsForLoading[loadingAnimation],
                  className,
                )}
                {...onLoadAnimations[onLoadAnimation].skeleton}
                transition={{ duration: onLoadDuration }}
              >
                {loaderChildren}
              </motion.div>
            ))}
        </React.Fragment>
      ) : (
        <motion.section
          key="content"
          className="text-[0] leading-[0]"
          {...onLoadAnimations[onLoadAnimation].content}
          transition={{ duration: onLoadDuration }}
        >
          {children}
        </motion.section>
      )}
    </AnimatePresence>
  );
}
